import * as XLSX from "xlsx";
import { Invoice } from "./../API";
import { getInvoice } from "./../graphql/queries";
import {
  Concept,
  Customer,
  InvoiceLine,
  Item,
  ModelInvoiceFilterInput,
  UpdateInvoiceInput,
} from "./../models/GQL_API";
import { deleteInvoice, updateInvoice } from "./../graphql/mutations";
import { API } from "aws-amplify";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { useDispatch, useSelector, useStore } from "react-redux";
import {
  setListing,
  setListingInvoicesStats,
  setListingCreditNotesStats,
  setSearchText,
  setSelected,
  setNextToken,
  changeLimit,
  setPreviousToken,
  nextAction,
  setCarryOverListing,
  setInvoiceListResume,
} from "../store/ducks/invoice";
import { HeadCell } from "../models/dataTable";
import useApp from "./useApp";
import {
  listInvoicesCustom,
  getInvoiceCustom,
  invoiceByConceptDateCustom,
  invoiceByConceptCustom,
} from "../graphqlCustom/queries";
import { fetchInvoiceTrashMetaForMutation } from "../helpers/fetchInvoiceTrashMeta";
import { buildInvoiceUpdateWithOptionalVersion } from "../helpers/invoiceMutationVersion";
import {
  ListingByAccountVariables,
  TransactionListingVariables,
  TransactionStats,
} from "../models/app";
import {
  INVOICE_STATUS,
  INVOICE_TYPES,
  TimelineActions,
  isCreditNoteDocument,
  isPendingInvoiceStatus,
} from "../constants/enums";
import { EG_OPERATIONS, EG_SEND_INVOICE } from "../constants/endPoints";
import useTimeline from "./useTimeline";
import { getFormattedDate } from "../helpers/utils";
import useCustomer from "./useCustomer";
import useItems from "./useItem";
import useConcept from "./useConcept";

/** AppSync/Dynamo often returns fewer than `limit` usable rows because filters drop items; chase nextToken automatically. */
const MAX_EMPTY_FILTERED_PAGES = 100;
/** Dynamo may return sparse pages (e.g. 2 rows) while nextToken implies more — merge responses until listing reaches `limit`. */
const MAX_FILL_PAGE_MERGE = 50;

/** AppSync/Dynamo `eq` on String is case-sensitive. ETA sync and some paths persist Title Case (`Invalid`) while the UI filter uses enum lowercase (`invalid`). */
function buildInvoiceStatusFilterFragment(invoiceStatus: string): any | null {
  const raw = String(invoiceStatus ?? "").trim();
  if (!raw || raw === "ALL") return null;
  const lower = raw.toLowerCase();
  const titled =
    lower.length > 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
  const variants = new Set<string>([lower, titled]);
  // Legacy compatibility: rows persisted before the rename use "pendingAdminApproval".
  if (lower === "pending") {
    variants.add("pendingAdminApproval");
    variants.add("PendingAdminApproval");
  }
  const variantList = Array.from(variants);
  if (variantList.length === 1) return { status: { eq: variantList[0] } };
  return { or: variantList.map((v) => ({ status: { eq: v } })) };
}

function sanitizeInvoiceRecord(invoice: any): any {
  if (!invoice) return invoice;
  if (invoice.invoiceLines && Array.isArray(invoice.invoiceLines)) {
    invoice.invoiceLines = invoice.invoiceLines.map((line: any) => {
      if (line && line.taxableItems && Array.isArray(line.taxableItems)) {
        line.taxableItems = line.taxableItems
          .filter((tax: any) => tax && tax.taxType != null && tax.subType != null)
          .map((tax: any) => ({
            ...tax,
            taxType: tax.taxType || "",
            subType: tax.subType || "",
          }));
      }
      return line;
    });
  }
  if (invoice.invoiceNeglectedLines && Array.isArray(invoice.invoiceNeglectedLines)) {
    invoice.invoiceNeglectedLines = invoice.invoiceNeglectedLines.map((line: any) => {
      if (line && line.taxableItems && Array.isArray(line.taxableItems)) {
        line.taxableItems = line.taxableItems
          .filter((tax: any) => tax && tax.taxType != null && tax.subType != null)
          .map((tax: any) => ({
            ...tax,
            taxType: tax.taxType || "",
            subType: tax.subType || "",
          }));
      }
      return line;
    });
  }
  return invoice;
}

const createInvoiceQueueMutationApprove = /* GraphQL */ `
  mutation CreateInvoiceQueue($input: CreateInvoiceQueueInput!) {
    createInvoiceQueue(input: $input) {
      id
      billNo
      payloadJson
      status
      createdAt
    }
  }
`;

// Large Opera payloads (group folios) exceed DynamoDB's 400 KB item cap, so
// the Lambda stores them as "GZ64:" + base64(gzip(json)). The same envelope is
// used when re-enqueueing to InvoiceQueue; the Java listener decompresses it.
const GZ64_PREFIX = "GZ64:";
const GZ64_THRESHOLD = 300000;

async function gunzipBase64(packed: string): Promise<string> {
  const bin = atob(packed.slice(GZ64_PREFIX.length));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const ds = new (window as any).DecompressionStream("gzip");
  const stream = (new Blob([bytes]).stream() as any).pipeThrough(ds);
  return await new Response(stream).text();
}

async function gzipToBase64(text: string): Promise<string> {
  const cs = new (window as any).CompressionStream("gzip");
  const stream = (new Blob([text]).stream() as any).pipeThrough(cs);
  const buf = await new Response(stream).arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return GZ64_PREFIX + btoa(bin);
}

// Ad-hoc selection: the list queries do not select originalPayloadJson yet
// (the field was just added to the schema). Fetch it explicitly at approve time.
const getInvoiceOriginalPayloadQuery = /* GraphQL */ `
  query GetInvoiceOriginalPayload($id: ID!) {
    getInvoice(id: $id) {
      id
      status
      internalID
      type
      documentType
      originalPayloadJson
    }
  }
`;

const useInvoice = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const store = useStore();
  const { session, showConfirm, showError } = useApp();
  const { conceptsGet } = useConcept("concepts", "concept");
  const { timelinesCreate } = useTimeline("timelines", "timeline");
  const { customersFetch } = useCustomer("customers", "customer");
  const { itemsFetch } = useItems("items", "item");

  const listing = useSelector(
    (state: any) => state[`${listingName}`]["listing"]
  );
  const searchText = useSelector(
    (state: any) => state[`${listingName}`]["searchText"]
  );

  const selected = useSelector(
    (state: any) => state[`${listingName}`]["selected"]
  );

  const storedLimit = useSelector(
    (state: any) => state[`${listingName}`]["limit"]
  );

  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]["nextToken"]
  );
  const previousTokens = useSelector(
    (state: any) => state[`${listingName}`]["previousTokens"]
  );
  async function fetch(props: TransactionListingVariables) {
    try {
      const {
        startIndex,
        limit,
        moveForward,
        conceptID,
        searchText,
        type,
        fromDate,
        toDate,
        conceptsSelectedFilters,
        invoiceStatus,
        requestScopeKey,
        requestGuard,
      } = props;

      const isCurrentRequest = () =>
        !requestGuard || requestGuard.isCurrent(requestGuard.id);
      //build
      const liveBucketAtStart = (store.getState() as any)[listingName] ?? {};
      const liveStoredLimit = liveBucketAtStart.limit ?? storedLimit;

      let requestLimit = liveStoredLimit;
      let requestToken = nextToken;
      let requestPreviousTokens = previousTokens;

      // When searching, use a larger limit to find matching invoices across all data
      if (searchText && searchText.trim().length > 0) {
        requestLimit = 10000;
        requestToken = null;
        requestPreviousTokens = [];
        if (listingName === "invoices") {
          dispatch(setCarryOverListing([]));
          dispatch(setInvoiceListResume(null));
        }
      }
      // Clear pagination data
      else if (limit !== liveStoredLimit || startIndex === 0) {
        requestLimit = limit;
        requestToken = null;
        requestPreviousTokens = [];

        dispatch(changeLimit(limit));
      }

      // Get token from previous tokens (back navigation)
      if (!moveForward) {
        const updatedPreviousTokens = [...requestPreviousTokens];
        updatedPreviousTokens.pop();

        requestToken =
          updatedPreviousTokens.length >= 2
            ? updatedPreviousTokens[updatedPreviousTokens.length - 2]
            : null;
        dispatch(
          setNextToken(updatedPreviousTokens[updatedPreviousTokens.length - 1])
        );
        dispatch(setPreviousToken(updatedPreviousTokens));
        if (listingName === "invoices") {
          dispatch(setCarryOverListing([]));
          dispatch(setInvoiceListResume(null));
        }
      } else if (
        listingName === "invoices" &&
        !(searchText && searchText.trim().length > 0)
      ) {
        // Forward paging: Redux `nextToken` is the cursor AFTER the page we are viewing.
        // AppSync expects the token that yields THIS page → previousTokens[pageIndex - 1], or null for page 1.
        const liveBucket = (store.getState() as any)[listingName] ?? {};
        const stack: any[] = Array.isArray(liveBucket.previousTokens)
          ? [...liveBucket.previousTokens]
          : [...requestPreviousTokens];
        const pageIdx = Math.floor(startIndex / limit);
        if (pageIdx <= 0) {
          requestToken = null;
        } else if (stack.length >= pageIdx) {
          requestToken = stack[pageIdx - 1];
        } else {
          requestToken = null;
        }
        requestPreviousTokens = stack.slice(0, pageIdx);
      }

      // One-shot restore: exact GraphQL cursor for this page (previousTokens depth can drift after merge/chase).
      if (
        listingName === "invoices" &&
        moveForward &&
        !(searchText && searchText.trim().length > 0)
      ) {
        const live = (store.getState() as any)[listingName] ?? {};
        const resume = live.invoiceListResume;
        const pageIdxNow = Math.floor(startIndex / limit);
        if (
          resume &&
          typeof resume.pageIndex === "number" &&
          resume.pageIndex === pageIdxNow &&
          (resume.limit === undefined || resume.limit === limit) &&
          (!resume.scopeKey ||
            !requestScopeKey ||
            resume.scopeKey === requestScopeKey)
        ) {
          requestToken = resume.inputCursor ?? null;
          dispatch(setInvoiceListResume(null));
        } else if (resume) {
          dispatch(setInvoiceListResume(null));
        }
      }

      const inputCursorCommitted = requestToken;

      // Carry-over MUST be read from fresh Redux state after the resets above.
      // useSelector snapshots from render still hold pre-reset carry on page 1 —
      // that wrongly prepends rows from a previous filter (e.g. "Invalid" showing "submitted").
      let pendingCarry: any[] = [];
      if (listingName === "invoices") {
        const bucket = (store.getState() as any)[listingName] ?? {};
        pendingCarry = [...(bucket.carryOverListing ?? [])];
        if (pendingCarry.length > 0) {
          dispatch(setCarryOverListing([]));
        }
      }

      const filter: any = {
        deleted: { eq: "0" },
      };

      if (type) {
        if (type === "Invoice") {
          filter.or = [
            { type: { eq: "Invoice" } },
            { type: { eq: "Credit Note" } },
            { type: { eq: "Credit" } },
          ];
        } else {
          filter.type = { eq: type };
        }
      }

      const conditions: any[] = [];

      // Search Condition
      if (searchText) {
        // Only add to filter if not using as a key condition
        if (!conceptID) {
          conditions.push({ internalID: { contains: searchText } });
        }
      }

      // Concepts Filter
      if (conceptsSelectedFilters && conceptsSelectedFilters.length > 0) {
        // Search across multiple concepts (OR logic) - this uses listInvoices query
        const conceptOr = conceptsSelectedFilters.map((c: any) => ({
          conceptID: { eq: c.id },
        }));
        conditions.push({ or: conceptOr });
      }

      if (invoiceStatus && invoiceStatus !== "ALL") {
        const statusCond = buildInvoiceStatusFilterFragment(invoiceStatus);
        if (statusCond) conditions.push(statusCond);
        const want = String(invoiceStatus).toLowerCase();
        pendingCarry = pendingCarry.filter(
          (inv: any) =>
            inv && String(inv.status ?? "").toLowerCase() === want
        );
      }

      // Date Filter - ONLY add to filter if NOT using invoiceByConceptDate index
      // If conceptID is present, we will use it as a KeyCondition variable instead
      if (fromDate && toDate && !conceptID) {
        conditions.push({
          createdAt: { ge: new Date(fromDate).getTime().toString() },
        });
        conditions.push({
          createdAt: {
            lt: new Date(toDate).getTime().toString(),
          },
        });
      }

      if (conditions.length > 0) {
        filter.and = conditions;
      }

      // Prepare Key Conditions for Index Queries
      let createdAtCondition: any = null;
      if (conceptID && fromDate && toDate) {
        createdAtCondition = {
          between: [
            new Date(fromDate).getTime().toString(),
            new Date(toDate).getTime().toString(),
          ],
        };
      }

      let invoicesList: any;
      console.log('[useInvoice] Fetching with searchText:', searchText, 'filter:', JSON.stringify(filter, null, 2));

      const executeInvoiceGraphql = async (
        pageToken: string | null | undefined
      ): Promise<any> => {
        try {
          if (
            conceptID &&
            (!conceptsSelectedFilters || conceptsSelectedFilters.length === 0)
          ) {
            if (searchText && searchText.trim().length > 0) {
              console.log(
                '[useInvoice] Using invoiceByConcept index for SEARCH with conceptID:',
                conceptID
              );

              const queryFilter = JSON.parse(JSON.stringify(filter));
              if (queryFilter.and) {
                queryFilter.and = queryFilter.and.filter(
                  (c: any) => !c.internalID && !c.conceptID
                );
                if (queryFilter.and.length === 0) delete queryFilter.and;
              }

              return await API.graphql<Invoice>({
                query: invoiceByConceptCustom,
                variables: {
                  conceptID,
                  internalID: { beginsWith: searchText },
                  sortDirection: "DESC",
                  filter: queryFilter,
                  limit: requestLimit,
                  nextToken: pageToken,
                },
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
              });
            }

            console.log(
              '[useInvoice] Using invoiceByConceptDate index for conceptID:',
              conceptID,
              'Dates:',
              fromDate,
              'to',
              toDate
            );

            const queryFilter = JSON.parse(JSON.stringify(filter));
            if (queryFilter.and) {
              queryFilter.and = queryFilter.and.filter(
                (c: any) => !c.createdAt && !c.conceptID
              );
              if (queryFilter.and.length === 0) delete queryFilter.and;
            }

            return await API.graphql<Invoice>({
              query: invoiceByConceptDateCustom,
              variables: {
                conceptID,
                createdAt: createdAtCondition,
                sortDirection: "DESC",
                filter: queryFilter,
                limit: requestLimit,
                nextToken: pageToken,
              },
              authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            });
          }

          return await API.graphql<Invoice>({
            query: listInvoicesCustom,
            variables: {
              filter,
              limit: requestLimit,
              nextToken: pageToken,
            },
            authMode: true
              ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
              : GRAPHQL_AUTH_MODE.AWS_IAM,
          });
        } catch (e: any) {
          console.error("[useInvoice] GraphQL error:", e);
          if (e?.data?.listInvoices) {
            return { data: { listInvoices: e.data.listInvoices } };
          }
          if (e?.data?.invoiceByConceptDate) {
            return {
              data: { invoiceByConceptDate: e.data.invoiceByConceptDate },
            };
          }
          if (e?.data?.invoiceByConcept) {
            return { data: { invoiceByConcept: e.data.invoiceByConcept } };
          }
          throw e;
        }
      };

      const pickConnectionBundle = (data: any) => {
        if (!data) return { items: [] as any[], nextToken: null as string | null | undefined };
        if (data.invoiceByConceptDate) {
          return {
            items: data.invoiceByConceptDate.items || [],
            nextToken: data.invoiceByConceptDate.nextToken,
          };
        }
        if (data.invoiceByConcept) {
          return {
            items: data.invoiceByConcept.items || [],
            nextToken: data.invoiceByConcept.nextToken,
          };
        }
        if (data.listInvoices) {
          return {
            items: data.listInvoices.items || [],
            nextToken: data.listInvoices.nextToken,
          };
        }
        return { items: [], nextToken: null };
      };

      const forwardCompactMode =
        moveForward && !(searchText && searchText.trim().length > 0);

      const fetchWithEmptyChaseToken = async (
        token: string | null | undefined
      ): Promise<any> => {
        let resp = await executeInvoiceGraphql(token);
        if (!forwardCompactMode) {
          return resp;
        }
        let skips = 0;
        while (skips < MAX_EMPTY_FILTERED_PAGES) {
          const conn = pickConnectionBundle(resp?.data);
          const rawCount = (conn.items || []).filter((it: any) => it).length;
          const nt = conn.nextToken ?? null;
          if (rawCount > 0 || !nt) break;
          skips++;
          resp = await executeInvoiceGraphql(nt);
        }
        return resp;
      };

      invoicesList = await fetchWithEmptyChaseToken(requestToken);

      let continuationTok: string | null | undefined = null;

      if (forwardCompactMode && listingName === "invoices") {
        const mergedCleaned: any[] = [...pendingCarry];

        const appendChunkCleaned = (rawItems: any[]) => {
          for (const inv of rawItems) {
            if (!inv) continue;
            mergedCleaned.push(sanitizeInvoiceRecord(inv));
          }
        };

        const b0 = pickConnectionBundle(invoicesList?.data);
        appendChunkCleaned(b0.items || []);
        continuationTok = b0.nextToken ?? null;

        let merges = 0;
        while (
          mergedCleaned.length < requestLimit &&
          continuationTok &&
          merges < MAX_FILL_PAGE_MERGE
        ) {
          merges++;
          invoicesList = await fetchWithEmptyChaseToken(continuationTok);
          const bn = pickConnectionBundle(invoicesList?.data);
          appendChunkCleaned(bn.items || []);
          continuationTok = bn.nextToken ?? null;
        }

        const pageSlice = mergedCleaned.slice(0, requestLimit);
        const remainder = mergedCleaned.slice(requestLimit);
        if (!isCurrentRequest()) return [];

        dispatch(setCarryOverListing(remainder));

        if (
          moveForward &&
          (invoicesList?.data?.listInvoices ||
            invoicesList?.data?.invoiceByConceptDate ||
            invoicesList?.data?.invoiceByConcept)
        ) {
          dispatch(nextAction(continuationTok, requestPreviousTokens));
        }

        dispatch(
          setInvoiceListResume({
            pageIndex: Math.floor(startIndex / limit),
            inputCursor: inputCursorCommitted ?? null,
            limit,
            scopeKey: requestScopeKey,
          })
        );

        return pageSlice;
      }

      if (
        moveForward &&
        (invoicesList?.data?.listInvoices ||
          invoicesList?.data?.invoiceByConceptDate ||
          invoicesList?.data?.invoiceByConcept)
      ) {
        continuationTok =
          invoicesList.data.listInvoices?.nextToken ||
          invoicesList.data.invoiceByConceptDate?.nextToken ||
          invoicesList.data.invoiceByConcept?.nextToken;
        if (!isCurrentRequest()) return [];
        dispatch(nextAction(continuationTok, requestPreviousTokens));
      }

      if (!isCurrentRequest()) return [];

      if (
        listingName === "invoices" &&
        moveForward &&
        !(searchText && searchText.trim().length > 0)
      ) {
        dispatch(
          setInvoiceListResume({
            pageIndex: Math.floor(startIndex / limit),
            inputCursor: inputCursorCommitted ?? null,
            limit,
            scopeKey: requestScopeKey,
          })
        );
      }

      const listing = (
        invoicesList?.data?.listInvoices?.items ||
        invoicesList?.data?.invoiceByConceptDate?.items ||
        invoicesList?.data?.invoiceByConcept?.items ||
        []
      ).filter((it: any) => it);

      return listing.map((invoice: any) => sanitizeInvoiceRecord(invoice));
    } catch (err: Error | any) {
      if (props.requestGuard && !props.requestGuard.isCurrent(props.requestGuard.id)) {
        return [];
      }
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
      console.log(err);
    }
  }

  /**
   * Export-only fetch: paginates through every page of invoices matching the given filters
   * and returns the full flat list. Does not touch Redux pagination state, so it is safe to
   * call from "Export All Data" without disturbing the on-screen list cursor.
   *
   * Mirrors fetch()'s filter / index-selection logic but uses a fresh nextToken loop instead
   * of Redux tokens / carry-over.
   */
  async function fetchAll(
    props: TransactionListingVariables & { pageSize?: number; maxItems?: number }
  ): Promise<Invoice[]> {
    try {
      const {
        conceptID,
        searchText,
        type,
        fromDate,
        toDate,
        conceptsSelectedFilters,
        invoiceStatus,
        pageSize,
        maxItems,
      } = props;

      const PAGE_SIZE = pageSize && pageSize > 0 ? pageSize : 1000;
      const HARD_CAP = maxItems && maxItems > 0 ? maxItems : 1_000_000;
      const PAGE_LIMIT_GUARD = 5000;

      const filter: any = { deleted: { eq: "0" } };

      if (type) {
        if (type === "Invoice") {
          filter.or = [
            { type: { eq: "Invoice" } },
            { type: { eq: "Credit Note" } },
            { type: { eq: "Credit" } },
          ];
        } else {
          filter.type = { eq: type };
        }
      }

      const conditions: any[] = [];
      if (searchText && !conceptID) {
        conditions.push({ internalID: { contains: searchText } });
      }
      if (conceptsSelectedFilters && conceptsSelectedFilters.length > 0) {
        conditions.push({
          or: conceptsSelectedFilters.map((c: any) => ({
            conceptID: { eq: c.id },
          })),
        });
      }
      if (invoiceStatus && invoiceStatus !== "ALL") {
        const statusCond = buildInvoiceStatusFilterFragment(invoiceStatus);
        if (statusCond) conditions.push(statusCond);
      }
      if (fromDate && toDate && !conceptID) {
        conditions.push({
          createdAt: { ge: new Date(fromDate).getTime().toString() },
        });
        conditions.push({
          createdAt: { lt: new Date(toDate).getTime().toString() },
        });
      }
      if (conditions.length > 0) filter.and = conditions;

      let createdAtCondition: any = null;
      if (conceptID && fromDate && toDate) {
        createdAtCondition = {
          between: [
            new Date(fromDate).getTime().toString(),
            new Date(toDate).getTime().toString(),
          ],
        };
      }

      const useByConceptSearch =
        !!(conceptID &&
          (!conceptsSelectedFilters || conceptsSelectedFilters.length === 0) &&
          searchText &&
          searchText.trim().length > 0);
      const useByConceptDate =
        !!(conceptID &&
          (!conceptsSelectedFilters || conceptsSelectedFilters.length === 0) &&
          !(searchText && searchText.trim().length > 0));

      const runPage = async (nextTok: string | null | undefined) => {
        try {
          if (useByConceptSearch) {
            const queryFilter = JSON.parse(JSON.stringify(filter));
            if (queryFilter.and) {
              queryFilter.and = queryFilter.and.filter(
                (c: any) => !c.internalID && !c.conceptID
              );
              if (queryFilter.and.length === 0) delete queryFilter.and;
            }
            return await API.graphql<Invoice>({
              query: invoiceByConceptCustom,
              variables: {
                conceptID,
                internalID: { beginsWith: searchText },
                sortDirection: "DESC",
                filter: queryFilter,
                limit: PAGE_SIZE,
                nextToken: nextTok,
              },
              authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            });
          }

          if (useByConceptDate) {
            const queryFilter = JSON.parse(JSON.stringify(filter));
            if (queryFilter.and) {
              queryFilter.and = queryFilter.and.filter(
                (c: any) => !c.createdAt && !c.conceptID
              );
              if (queryFilter.and.length === 0) delete queryFilter.and;
            }
            return await API.graphql<Invoice>({
              query: invoiceByConceptDateCustom,
              variables: {
                conceptID,
                createdAt: createdAtCondition,
                sortDirection: "DESC",
                filter: queryFilter,
                limit: PAGE_SIZE,
                nextToken: nextTok,
              },
              authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            });
          }

          return await API.graphql<Invoice>({
            query: listInvoicesCustom,
            variables: { filter, limit: PAGE_SIZE, nextToken: nextTok },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          });
        } catch (e: any) {
          if (e?.data?.listInvoices)
            return { data: { listInvoices: e.data.listInvoices } };
          if (e?.data?.invoiceByConceptDate)
            return { data: { invoiceByConceptDate: e.data.invoiceByConceptDate } };
          if (e?.data?.invoiceByConcept)
            return { data: { invoiceByConcept: e.data.invoiceByConcept } };
          throw e;
        }
      };

      const collected: any[] = [];
      let token: string | null | undefined = null;
      let pages = 0;
      do {
        const resp: any = await runPage(token);
        const bundle =
          resp?.data?.invoiceByConceptDate ||
          resp?.data?.invoiceByConcept ||
          resp?.data?.listInvoices ||
          { items: [], nextToken: null };
        const items: any[] = (bundle.items || []).filter((it: any) => it);
        for (const inv of items) {
          collected.push(sanitizeInvoiceRecord(inv));
          if (collected.length >= HARD_CAP) break;
        }
        token = bundle.nextToken ?? null;
        pages += 1;
        if (collected.length >= HARD_CAP) break;
      } while (token && pages < PAGE_LIMIT_GUARD);

      return collected;
    } catch (err: any) {
      showError(
        typeof err?.message === "string" ? err.message : "Error occurred"
      );
      console.log(err);
      return [];
    }
  }

  /*
   * Fetch all invoice within selected range
   * Filter then using invoice status (Valid, Invalid, Rejected, Cancelled)
   * Return Hash map contains (Status, # of invoices, total, percentage)
   */
  async function fetchStats(props: TransactionListingVariables) {
    try {
      const {
        limit,
        conceptID,
        fromDate,
        toDate,
        conceptsSelectedFilters,
        type,
      } = props;

      const filter: ModelInvoiceFilterInput = {
        deleted: { eq: "0" },
      };

      if (type) {
        filter.type = { eq: type };
      }

      if (conceptsSelectedFilters && conceptsSelectedFilters.length > 0) {
        // Concepts Filter
        filter.or = [];
        for (let conceptsSelected of conceptsSelectedFilters) {
          filter.or.push({ conceptID: { eq: conceptsSelected.id } });
        }
      } else {
        if (conceptID) filter.conceptID = { eq: conceptID };
      }

      // Date Filter
      if (fromDate && toDate) {
        filter.and = [];

        filter.and.push({
          createdAt: { ge: new Date(fromDate).getTime().toString() },
        });
        filter.and.push({
          createdAt: {
            lt: new Date(toDate).getTime().toString(),
          },
        });
      }

      let invoiceList: any;
      try {
        invoiceList = await API.graphql<Invoice>({
          query: listInvoicesCustom,
          variables: { filter, limit },
          authMode: true
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });
      } catch (e: any) {
        console.error("[useInvoice] GraphQL error in fetchStats:", e);
        // Extract data from error response if available (GraphQL can return partial data with errors)
        if (e?.data?.listInvoices) {
          invoiceList = { data: { listInvoices: e.data.listInvoices } };
        } else {
          throw e;
        }
      }

      const listing = (invoiceList?.data?.listInvoices?.items || []).filter(
        (it: any) => it
      );

      // Clean up invoices: filter out null taxableItems and provide defaults for null taxType/subType
      const cleanedListing = listing.map((invoice: any) => {
        if (!invoice) return invoice;
        
        // Clean invoiceLines
        if (invoice.invoiceLines && Array.isArray(invoice.invoiceLines)) {
          invoice.invoiceLines = invoice.invoiceLines.map((line: any) => {
            if (line && line.taxableItems && Array.isArray(line.taxableItems)) {
              line.taxableItems = line.taxableItems
                .filter((tax: any) => tax && tax.taxType != null && tax.subType != null)
                .map((tax: any) => ({
                  ...tax,
                  taxType: tax.taxType || '',
                  subType: tax.subType || '',
                }));
            }
            return line;
          });
        }
        
        // Clean invoiceNeglectedLines
        if (invoice.invoiceNeglectedLines && Array.isArray(invoice.invoiceNeglectedLines)) {
          invoice.invoiceNeglectedLines = invoice.invoiceNeglectedLines.map((line: any) => {
            if (line && line.taxableItems && Array.isArray(line.taxableItems)) {
              line.taxableItems = line.taxableItems
                .filter((tax: any) => tax && tax.taxType != null && tax.subType != null)
                .map((tax: any) => ({
                  ...tax,
                  taxType: tax.taxType || '',
                  subType: tax.subType || '',
                }));
            }
            return line;
          });
        }
        
        return invoice;
      });

      // Calculate stats
      let DocumentType = type;
      if (DocumentType === undefined) DocumentType = INVOICE_TYPES.INVOICE;

      const stats = await InvoicesStats(cleanedListing, DocumentType);

      return stats;
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
      console.log(err);
    }
  }

  async function InvoicesStats(listing: Invoice[], type: string) {
    try {
      let submitCounter: TransactionStats = {
        id: 0,
        status: INVOICE_STATUS.SUBMITTED,
        counter: 0,
        total: 0,
        percentage: 0,
        fill: "#B835E0",
      };

      let validCounter: TransactionStats = {
        id: 1,
        status: INVOICE_STATUS.VALID,
        counter: 0,
        total: 0,
        percentage: 0,
        fill: "#84c529",
      };

      let rejectedCounter: TransactionStats = {
        id: 2,
        status: INVOICE_STATUS.REJECTED,
        counter: 0,
        total: 0,
        percentage: 0,
        fill: "#03a9f4",
      };

      let cancelledCounter: TransactionStats = {
        id: 3,
        status: INVOICE_STATUS.CANCELLED,
        counter: 0,
        total: 0,
        percentage: 0,
        fill: "#ff6f00",
      };

      let invalidCounter: TransactionStats = {
        id: 4,
        status: INVOICE_STATUS.INVALID,
        counter: 0,
        total: 0,
        percentage: 0,
        fill: "#fc2d42",
      };

      let totalCounter: TransactionStats = {
        id: 5,
        status: "total",
        counter: 0,
        total: 0,
        percentage: 100,
        fill: "#172329",
      };

      let totalInvoices = 0;
      let totalAmount = 0;

      listing.forEach(({ status, totalAmount }) => {
        switch (status) {
          case INVOICE_STATUS.SUBMITTED:
            submitCounter.counter++;
            submitCounter.total += totalAmount;
            break;
          case INVOICE_STATUS.VALID:
            validCounter.counter++;
            validCounter.total += totalAmount;
            break;
          case INVOICE_STATUS.REJECTED:
            rejectedCounter.counter++;
            rejectedCounter.total += totalAmount;
            break;
          case INVOICE_STATUS.CANCELLED:
            cancelledCounter.counter++;
            cancelledCounter.total += totalAmount;
            break;
          case INVOICE_STATUS.INVALID:
            invalidCounter.counter++;
            invalidCounter.total += totalAmount;
            break;
          default:
            break;
        }
      });

      totalInvoices =
        submitCounter.counter +
        validCounter.counter +
        invalidCounter.counter +
        rejectedCounter.counter +
        cancelledCounter.counter;

      totalAmount =
        submitCounter.total +
        validCounter.total +
        invalidCounter.total +
        rejectedCounter.total +
        cancelledCounter.total;

      totalCounter.counter = totalInvoices;
      totalCounter.total = totalAmount;

      if (totalInvoices > 0) {
        submitCounter.percentage =
          (submitCounter.counter / totalInvoices) * 100;
        validCounter.percentage = (validCounter.counter / totalInvoices) * 100;
        invalidCounter.percentage =
          (invalidCounter.counter / totalInvoices) * 100;
        rejectedCounter.percentage =
          (rejectedCounter.counter / totalInvoices) * 100;
        cancelledCounter.percentage =
          (cancelledCounter.counter / totalInvoices) * 100;
      }

      const InvoicesStats: TransactionStats[] = [
        submitCounter,
        validCounter,
        rejectedCounter,
        cancelledCounter,
        invalidCounter,
        totalCounter,
      ];

      if (type === INVOICE_TYPES.INVOICE)
        dispatch(setListingInvoicesStats(InvoicesStats));
      else if (type === INVOICE_TYPES.CREDIT_NOTE)
        dispatch(setListingCreditNotesStats(InvoicesStats));

      return InvoicesStats;
    } catch (err: Error | any) {
      throw err;
    }
  }

  async function get(id: string, bypassCache: boolean = false) {
    try {
      let single: Invoice | undefined;
      if (listing.length !== 0 && !bypassCache) {
        single = listing.find((resource: any) => resource.id === id);
      }

      if (single === undefined) {
        const listing: any = await API.graphql<Invoice>({
          query: getInvoiceCustom,
          variables: { id },
          authMode: true
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });
        single = listing.data.getInvoice;
      }

      return single;
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
      console.log(err);
    }
  }

  async function update(
    id: string,
    data: any,
    editedLine: any,
    action: string
  ) {
    try {
      const original = await get(id);
      if (original) {
        let updateInput: UpdateInvoiceInput = {
          id: original.id,

          dateTimeIssued: data.dateTimeIssued
            ? data.dateTimeIssued
            : original!.dateTimeIssued,
          totalDiscountAmount: data.totalDiscountAmount
            ? data.totalDiscountAmount
            : original!.totalDiscountAmount,
          totalSalesAmount: data.totalSalesAmount
            ? data.totalSalesAmount
            : original!.totalSalesAmount,
          netAmount: data.netAmount ? data.netAmount : original!.netAmount,
          totalAmount: data.totalAmount
            ? data.totalAmount
            : original!.totalAmount,
          extraDiscountAmount: data.extraDiscountAmount
            ? data.extraDiscountAmount
            : original!.extraDiscountAmount,
          totalItemsDiscountAmount: data.totalItemsDiscountAmount
            ? data.totalItemsDiscountAmount
            : original!.totalItemsDiscountAmount,
          invoiceLines: data.invoiceLines,
        };

        if (editedLine) {
          let timelineParams: any = {};

          // Edit (description, code, type, unit) only
          timelineParams = {
            actionName: TimelineActions.UPDATE_TRX_LINE,
            oldStatus: "",
            newStatus: "",
            transactionId: original.id,
          };
          await timelinesCreate(timelineParams);
        }

        await API.graphql<Invoice>({
          query: updateInvoice,
          variables: { input: updateInput },
          authMode: true
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });
      }

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function recalculateDocumentAfterDelete(data: Invoice, item: any) {
    let updateInvoice = { ...data };
    let newNet: number = updateInvoice.netAmount;
    let newTotalSales: number = updateInvoice.totalSalesAmount;
    let newTotalAmount: number = updateInvoice.totalAmount;

    let tax = item.total - item.totalTaxableFees - item.salesTotal;
    let serviceCharge = item?.totalTaxableFees ? item.totalTaxableFees : 0;

    newNet -= item?.netTotal ? item.netTotal : 0;
    newTotalSales -= item?.salesTotal ? item.salesTotal : 0;
    newTotalAmount -= item?.total ? item.total : 0;

    updateInvoice.netAmount = newNet;
    updateInvoice.totalSalesAmount = newTotalAmount;
    updateInvoice.totalAmount = newTotalSales; // (net + tax - discount)

    for (let taxItem of updateInvoice.taxTotals) {
      // 14.00
      if (taxItem && taxItem.taxType === "T1") {
        taxItem.amount -= tax;
      }
      // 12.00
      else if (taxItem && taxItem.taxType === "T9") {
        taxItem.amount -= serviceCharge;
      }
    }

    return updateInvoice;
  }

  async function trash(id: string) {
    try {
      const authMode = true
        ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
        : GRAPHQL_AUTH_MODE.AWS_IAM;

      const meta = await fetchInvoiceTrashMetaForMutation(id, authMode);

      if (!meta?.id) {
        showError(`${singleName} not found`);
        return false;
      }

      const updateInput = buildInvoiceUpdateWithOptionalVersion(meta.id, { deleted: "1" }, meta.mutationVersion);

      await API.graphql<Invoice>({
        query: updateInvoice,
        variables: { input: updateInput as UpdateInvoiceInput },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      showConfirm(`${singleName} has been moved to trash successfully`);
      return true;
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
      console.log(err);
      return false;
    }
  }

  async function bulkTrash(resourceIds: any) {
    for (let id of resourceIds) {
      try {
        await trash(id);
      } catch (err: Error | any) {
        showError(err);
      }
    }

    dispatch(
      setListing(
        listing.filter((resource: any) => !resourceIds.has(resource.id))
      )
    );

    showConfirm(
      `${resourceIds.size} ${listingName} items has been moved to trash`
    );
  }

  async function remove(id: any) {
    try {
      await API.graphql<Invoice>({
        query: deleteInvoice,
        variables: { id: id },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      dispatch(
        setListing(listing.filter((resource: any) => resource.id !== id))
      );

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
      console.log(err);
    }
  }

  /*
   * Cancel document in Egyptian Gateway
   */
  async function cancelInvoice(document: Invoice, cancelReason: string) {
    try {
      if (
        document.status !== INVOICE_STATUS.SUBMITTED &&
        document.status !== INVOICE_STATUS.VALID
      ) {
        showError(`Can't cancel ${document.status} document`);
        return;
      }

      const branchId = document.conceptID;
      const operation = EG_OPERATIONS.CANCEL_INVOICE;
      const requestPayload = {
        createdByID: session ? session.sub : "",
        createdByName: session ? session.name : "",

        document: document,
        cancelReason: cancelReason ? cancelReason : "Invalid document.",
      };

      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestPayload),
      };

      await window
        .fetch(
          `${EG_SEND_INVOICE}?operation=${operation}&branchId=${branchId}`,
          requestOptions
        )
        .then((response) => response.text())
        .then((text) => {
          let responseBody = JSON.parse(text);
          if (responseBody.status === 200) {
            showConfirm(responseBody.message);
          } else {
            showError(responseBody.message);
          }
        })
        .catch((err) => {
          showError(
            typeof err.message === "string"
              ? err.message
              : "error occurred while sending SMS"
          );
        });
    } catch (err) {
      console.log(err);
    }
  }

  async function submitInvoice(document: Invoice) {
    try {
      const branchId = document.conceptID;
      const operation = EG_OPERATIONS.SUBMIT_INVOICE;
      const requestPayload = {
        createdByID: session ? session.sub : "",
        createdByName: session ? session.name : "",

        document: document,
      };

      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestPayload),
      };

      await window
        .fetch(
          `${EG_SEND_INVOICE}?operation=${operation}&branchId=${branchId}`,
          requestOptions
        )
        .then((response) => response.text())
        .then((text) => {
          let responseBody = JSON.parse(text);

          let StatusMessages = responseBody.StatusMessages.Messages;
          if (StatusMessages.length > 0) {
            let errorMessage = "";
            StatusMessages.forEach((message: any) => {
              errorMessage += message.Description;
            });

            showError(errorMessage);
          }
        })
        .catch((err) => {
          showError(
            typeof err.message === "string"
              ? err.message
              : "error occurred while sending SMS"
          );
        });
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
      console.log(err);
    }
  }

  // Admin approves a held B2B invoice. We re-enqueue the original Opera payload
  // (stashed on the held row at hold time) with adminApproved=true so the
  // Lambda's B2B check bypasses on the second pass, then soft-delete the held row.
  async function approveInvoice(document: Invoice) {
    try {
      if (!isPendingInvoiceStatus(document.status)) {
        showError(
          `Only invoices pending admin approval can be submitted to ETA. Current status: ${document.status}`
        );
        return;
      }

      // Fetch fresh — the list queries may not select originalPayloadJson.
      const authModePre = GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS;
      const heldResp: any = await API.graphql({
        query: getInvoiceOriginalPayloadQuery,
        variables: { id: document.id },
        authMode: authModePre,
      });
      const heldRow = heldResp?.data?.getInvoice as
        | (Invoice & { originalPayloadJson?: string })
        | null
        | undefined;
      let raw = heldRow?.originalPayloadJson || "";
      if (!raw) {
        showError(
          "This pending invoice has no stored payload. Use Edit to rebuild and submit."
        );
        return;
      }
      if (raw.startsWith(GZ64_PREFIX)) {
        try {
          raw = await gunzipBase64(raw);
        } catch (gzErr) {
          showError(
            "Stored payload could not be decompressed; use Edit to rebuild and submit."
          );
          return;
        }
      }

      let payload: any;
      try {
        payload = JSON.parse(raw);
      } catch (parseErr) {
        showError("Stored payload is malformed; use Edit to rebuild and submit.");
        return;
      }
      payload.adminApproved = true;

      // The Lambda decides invoice vs credit note purely from the folio header
      // (`FolioType === "CREDIT"` or `CreditBill === true`) — the held row's
      // `type` is never consulted on the second pass. Re-stamp the credit shape
      // so approving a held credit note can only ever reach ETA as a credit
      // note (documentType "c"), never as a fresh positive invoice.
      if (isCreditNoteDocument(heldRow) || isCreditNoteDocument(document)) {
        if (!payload.FolioInfo) payload.FolioInfo = {};
        if (!payload.FolioInfo.FolioHeaderInfo) {
          payload.FolioInfo.FolioHeaderInfo = {};
        }
        payload.FolioInfo.FolioHeaderInfo.FolioType = "CREDIT";
        payload.FolioInfo.FolioHeaderInfo.CreditBill = true;
      }

      const billNo =
        (payload.FolioInfo &&
          payload.FolioInfo.FolioHeaderInfo &&
          payload.FolioInfo.FolioHeaderInfo.FolioNo) ||
        document.internalID ||
        "";

      const authMode = GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS;

      // Soft-delete the held row first; on failure of the enqueue we roll back.
      let didSoftDelete = false;
      let rollbackVersion: number | undefined;
      try {
        const meta = await fetchInvoiceTrashMetaForMutation(document.id, authMode);
        if (!meta?.id) {
          showError("Pending invoice not found for approval");
          return;
        }
        const softDeleteInput = buildInvoiceUpdateWithOptionalVersion(
          document.id,
          { deleted: "1" },
          meta.mutationVersion
        );
        const delRes = (await API.graphql({
          query: updateInvoice,
          variables: { input: softDeleteInput },
          authMode,
        })) as { data?: { updateInvoice?: { _version?: number | null } } };
        rollbackVersion =
          typeof delRes?.data?.updateInvoice?._version === "number"
            ? delRes.data!.updateInvoice!._version!
            : undefined;
        didSoftDelete = true;

        // Re-compress large payloads: the InvoiceQueue row is also a DynamoDB
        // item (400 KB cap) and the AppSync mutation request caps at 1 MB.
        const queuePayloadPlain = JSON.stringify(payload);
        const queuePayload =
          queuePayloadPlain.length > GZ64_THRESHOLD
            ? await gzipToBase64(queuePayloadPlain)
            : queuePayloadPlain;

        await API.graphql({
          query: createInvoiceQueueMutationApprove,
          variables: {
            input: {
              billNo,
              payloadJson: queuePayload,
              status: "PENDING",
            },
          },
          authMode,
        });

        showConfirm("Invoice submitted to ETA pipeline");
      } catch (queueErr: any) {
        if (didSoftDelete) {
          try {
            let rv: number | undefined = rollbackVersion;
            if (rv === undefined) {
              const refetchMeta = await fetchInvoiceTrashMetaForMutation(
                document.id,
                authMode
              );
              rv = refetchMeta?.mutationVersion;
            }
            const rollbackInput = buildInvoiceUpdateWithOptionalVersion(
              document.id,
              { deleted: "0" },
              rv
            );
            await API.graphql({
              query: updateInvoice,
              variables: { input: rollbackInput },
              authMode,
            });
          } catch (rollbackErr) {
            console.error(
              "[Approve Invoice] CRITICAL: rollback of soft-delete failed",
              { invoiceId: document.id, rollbackErr }
            );
          }
        }
        throw queueErr;
      }
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
      console.log(err);
    }
  }

  async function resubmitInvoice(document: Invoice) {
    try {
      // Check document status
      if (
        document.status === INVOICE_STATUS.SUBMITTED ||
        document.status === INVOICE_STATUS.VALID
      ) {
        showError(`Can resubmit ${document.status} document.`);
        return;
      }

      const branchId = document.conceptID;
      const operation = EG_OPERATIONS.RESUBMIT_INVOICE;
      const requestPayload = {
        createdByID: session ? session.sub : "",
        createdByName: session ? session.name : "",

        document: document,
      };

      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestPayload),
      };

      await window
        .fetch(
          `${EG_SEND_INVOICE}?operation=${operation}&branchId=${branchId}`,
          requestOptions
        )
        .then((response) => response.text())
        .then((text) => {
          let responseBody = JSON.parse(text);

          let StatusMessages = responseBody.StatusMessages.Messages;
          if (StatusMessages.length > 0) {
            let errorMessage = "";
            StatusMessages.forEach((message: any) => {
              errorMessage += message.Description;
            });

            showError(errorMessage);
          }
        })
        .catch((err) => {
          showError(
            typeof err.message === "string"
              ? err.message
              : "error occurred while sending SMS"
          );
        });
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
      console.log(err);
    }
  }

  async function submitCreditNote(document: Invoice, invoiceLine: InvoiceLine) {
    try {
      // Check document status
      if (
        document.status !== INVOICE_STATUS.SUBMITTED &&
        document.status !== INVOICE_STATUS.VALID
      ) {
        showError(`Can send credit note for ${document.status} document.`);
        return;
      }

      const branchId = document.conceptID;
      const operation = EG_OPERATIONS.CANCEL_INVOICE_LINE;

      /* Create new credit note document */
      let taxTotals = [];
      if (invoiceLine.taxableItems)
        for (const taxLine of invoiceLine.taxableItems) {
          if (taxLine) {
            let tax = {
              taxType: taxLine.taxType,
              amount: taxLine.amount,
            };
            taxTotals.push(tax);
          }
        }

      let creditNoteDoc: any = {
        // Static Fields
        issuer: document.issuer,
        receiver: document.receiver,
        documentType: "C",
        documentTypeVersion: document.documentTypeVersion,
        dateTimeIssued: getFormattedDate(new Date()),
        taxpayerActivityCode: document.taxpayerActivityCode,
        internalID: document.internalID,
        // Zeros Fields
        totalDiscountAmount: document.totalDiscountAmount,
        extraDiscountAmount: document.extraDiscountAmount,
        totalItemsDiscountAmount: document.totalItemsDiscountAmount,
        // Calculated Fields
        invoiceLines: [invoiceLine],
        netAmount: invoiceLine.netTotal,
        totalSalesAmount: invoiceLine.salesTotal,
        totalAmount: invoiceLine.total,
        taxTotals: taxTotals,
      };

      const requestPayload = {
        createdByID: session ? session.sub : "",
        createdByName: session ? session.name : "",
        document: document,
        creditNoteDoc: creditNoteDoc,
      };

      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestPayload),
      };

      await window
        .fetch(
          `${EG_SEND_INVOICE}?operation=${operation}&branchId=${branchId}`,
          requestOptions
        )
        .then((response) => response.text())
        .then((text) => {
          let responseBody = JSON.parse(text);

          let StatusMessages = responseBody.StatusMessages.Messages;
          if (StatusMessages.length > 0) {
            let errorMessage = "";
            StatusMessages.forEach((message: any) => {
              errorMessage += message.Description;
            });

            showError(errorMessage);
          }
        })
        .catch((err) => {
          showError(
            typeof err.message === "string"
              ? err.message
              : "error occurred while sending SMS"
          );
        });
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
      console.log(err);
    }
  }

  /*
   * Get invoices listed in CSV format
   * Convert them to JSON
   * Save this JSON in local file
   * Loop over this list to submit invoice/credit note
   *
   * */
  async function importDataExcelFile(
    file: File,
    accountID: string,
    conceptID: string
  ) {
    let folioNo = "";
    let quantity = 0;
    let unitPrice = 0;
    let netAmount = 0;
    let salesAmount = 0;
    let tax = 0;
    let serviceCharge = 0;
    let grossAmount = 0;
    let currency = "";
    let exchangeRate = 0;
    let transactionCode = "";
    let accountNumber = "";
    let firstName = "";
    let lastName = "";
    let identificationID = "";

    let documentType = "C"; // I for Invoice, C for Credit Note
    let documentTypeVersion = "1.0";
    let date = "2023-10-30T00:00:00Z";
    let prefix = ``; // V for Village, I for Island

    let invoiceLines: any[] = [];
    let invoices: any[] = [];

    const listingByAccountParams: ListingByAccountVariables = {
      accountID: accountID,
      conceptID: "",
      searchText: "",
      startIndex: 0,
      limit: 1000,
      session,
    };

    try {
      /* Database requests for mapping tables */
      const databaseMapping = await Promise.all([
        conceptsGet(conceptID),
        customersFetch(listingByAccountParams),
        itemsFetch(listingByAccountParams),
      ]);

      const branch: Concept = databaseMapping[0];
      const customers = databaseMapping[1];
      const itemsMapping = databaseMapping[2];
      prefix = branch.prefix ? `${branch.prefix}-` : "";

      let issuer: any = {};
      if (branch && branch.branchAddress) {
        const branchAddress: any = {};
        branchAddress.branchId = branch.branchAddress.branchId;
        branchAddress.country = branch.branchAddress.country;
        branchAddress.governate = branch.branchAddress.governate;
        branchAddress.regionCity = branch.branchAddress.regionCity;
        branchAddress.street = branch.branchAddress.street;
        branchAddress.buildingNumber = branch.branchAddress.buildingNumber;
        branchAddress.postalCode = branch.branchAddress.postalCode;
        branchAddress.floor = branch.branchAddress.floor;
        branchAddress.room = branch.branchAddress.room;
        branchAddress.landmark = branch.branchAddress.landmark;
        branchAddress.additionalInformation =
          branch.branchAddress.additionalInformation;

        issuer.type = branch.type ? branch.type : "B";
        issuer.id = branch.rin;
        issuer.name = branch.companyTradeName;
        issuer.address = branchAddress;
      }

      const reader = new FileReader();
      reader.readAsBinaryString(file);

      reader.onload = async (event) => {
        const data = event.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet);

        let currentFolioNumber = "";
        let guestName = "";
        let arrivalDate = "";
        let departureDate = "";
        let roomNumber = "";

        let currentCustomer: any = null;
        let totalNet = 0;
        let totalTax = 0;
        let totalService = 0;
        let totalSalesAmount = 0;
        let totalAmount = 0;
        let rate = 1;

        for (let i = 0; i < rows.length; i++) {
          const row: any = rows[i];
          folioNo = row["folioNo"] || "";

          // Receiver Data
          accountNumber = row["accountNumber"] || "";
          firstName = row["firstName"] || "";
          lastName = row["lastName"] || "";
          firstName = firstName.replace("'", " ").trim();
          lastName = lastName.replace("'", " ").trim();

          identificationID = row["identificationID"] || "";

          // First Invoice Only
          if (currentFolioNumber === "") {
            currentFolioNumber = folioNo;
            guestName = (firstName + " " + lastName).trim();
            currentCustomer = await getDocumentCustomer(
              accountNumber,
              firstName,
              lastName,
              customers,
              identificationID
            );
          }

          if (currentFolioNumber !== folioNo) {
            // Loop over invoice lines and add foreign currency
            let convertCurrency = false;
            let invoiceCurrency = "SAR";
            let invoiceRate = 1;
            for (let invoiceLine of invoiceLines) {
              if (invoiceLine.unitValue.currencySold !== "SAR") {
                convertCurrency = true;
                invoiceCurrency = invoiceLine.unitValue.currencySold;
                invoiceRate = invoiceLine.unitValue.currencyExchangeRate;

                break;
              }
            }

            let updateInvoiceLines = [];
            if (convertCurrency) {
              for (let invoiceLine of invoiceLines) {
                invoiceLine = {
                  ...invoiceLine,
                  unitValue: {
                    amountEGP: invoiceLine.unitValue.amountEGP,
                    amountSold: +(
                      invoiceLine.unitValue.amountEGP / invoiceRate
                    ).toFixed(5),
                    currencyExchangeRate: +invoiceRate.toFixed(5),
                    currencySold: invoiceCurrency,
                  },
                };

                updateInvoiceLines.push(invoiceLine);
              }
            } else {
              updateInvoiceLines = invoiceLines;
            }

            // Add created invoice
            let invoice = {
              // Static Fields
              documentType: documentType,
              documentTypeVersion: documentTypeVersion,
              dateTimeIssued: date,

              issuer: issuer,
              receiver: currentCustomer.address
                ? {
                    type: currentCustomer.type,
                    id: currentCustomer.registrationNumber,
                    name: currentCustomer.name,
                    address: currentCustomer.address,
                  }
                : {
                    type: currentCustomer.type,
                    id: currentCustomer.registrationNumber || "",
                    name: currentCustomer.name,
                  },
              purchaseOrderReference:
                guestName !== "" ? "Guest Name: " + guestName : "",
              purchaseOrderDescription: `Arrival Date ${arrivalDate} Departure Date ${departureDate} Room Number ${roomNumber}`,

              taxpayerActivityCode: branch.activityCode,
              internalID: `${prefix}${currentFolioNumber.toString()}`,

              // Zeros Fields
              totalDiscountAmount: 0,
              extraDiscountAmount: 0,
              totalItemsDiscountAmount: 0,

              // Calculated Fields
              invoiceLines: updateInvoiceLines,
              netAmount: +totalNet.toFixed(5),
              totalSalesAmount: +totalSalesAmount.toFixed(5),
              totalAmount: +totalAmount.toFixed(5),
              taxTotals: [
                {
                  taxType: "T1",
                  amount: +totalTax.toFixed(5),
                },
                {
                  taxType: "T9",
                  amount: +totalService.toFixed(5),
                },
              ],
            };

            if (invoiceLines.length > 0) invoices.push(invoice);

            // Init new invoice
            totalNet = 0;
            totalTax = 0;
            totalService = 0;
            totalSalesAmount = 0;
            totalAmount = 0;
            invoiceLines = [];
            convertCurrency = false;

            currentFolioNumber = folioNo;
            guestName = (firstName + " " + lastName).trim();
            currentCustomer = await getDocumentCustomer(
              accountNumber,
              firstName,
              lastName,
              customers,
              identificationID
            );
          }

          netAmount = row["netAmount"] || 0;
          quantity = Math.abs(row["quantity"]) || 0;
          salesAmount = Math.abs(row["netAmount"]) || 0;
          tax = Math.abs(row["tax"]) || 0;
          serviceCharge = Math.abs(row["serviceCharge"]) || 0;
          grossAmount = Math.abs(row["grossAmount"]) || 0;
          unitPrice = +salesAmount / +quantity;
          currency = row["currency"] || "";
          exchangeRate = row["exchangeRate"] || "";
          transactionCode = row["transactionCode"] || "";

          // Reservation Info
          arrivalDate = row["arrivalDate"] || "";
          departureDate = row["departureDate"] || "";
          roomNumber = row["roomNumber"] || "";

          if (typeof arrivalDate === "number") {
            arrivalDate = await getDocumentDateFormatted(arrivalDate);
          }

          if (typeof departureDate === "number") {
            departureDate = await getDocumentDateFormatted(departureDate);
          }
          // Skip zero lines
          if (+netAmount === 0) {
            continue;
          }

          if (
            (documentType === "I" && +netAmount < 0) ||
            (documentType === "C" && +netAmount > 0)
          ) {
            continue;
          }
          netAmount = Math.abs(netAmount);

          totalTax += +tax;
          totalService += +serviceCharge;
          totalNet += +netAmount;
          totalSalesAmount += +salesAmount;
          totalAmount += +grossAmount;
          rate = +(1 / exchangeRate).toFixed(5);

          const itemMapping = itemsMapping.find(
            // eslint-disable-next-line no-loop-func
            (obj: Item) => obj.internalCode === transactionCode.toString()
          );

          let line = {
            description: itemMapping ? itemMapping.description : "",
            internalCode: transactionCode.toString(),
            itemCode: itemMapping ? itemMapping.itemCode : "",
            itemType: itemMapping ? itemMapping.type : "",
            unitType: itemMapping ? itemMapping.unit : "EA",

            itemsDiscount: 0,
            netTotal: +netAmount.toFixed(5),
            quantity: quantity,
            salesTotal: +salesAmount.toFixed(5),
            total: +grossAmount.toFixed(5),
            totalTaxableFees: +serviceCharge.toFixed(5),

            unitValue:
              currency !== "SAR"
                ? {
                    amountEGP: +unitPrice.toFixed(5),
                    amountSold: +(unitPrice / rate).toFixed(5),
                    currencyExchangeRate: rate,
                    currencySold: currency,
                  }
                : {
                    amountEGP: +unitPrice.toFixed(5),
                    currencySold: currency,
                  },
            valueDifference: 0,

            taxableItems: [
              {
                amount: +tax.toFixed(5),
                rate: 14,
                subType: "V001",
                taxType: "T1",
              },
              {
                amount: +serviceCharge.toFixed(5),
                rate: 12,
                subType: "SC01",
                taxType: "T9",
              },
            ],
          };

          invoiceLines.push(line);
        }

        // Add last invoice
        let invoice = {
          // Static Fields
          documentType: documentType,
          documentTypeVersion: documentTypeVersion,
          dateTimeIssued: date,

          issuer: issuer,
          receiver: currentCustomer.address
            ? {
                type: currentCustomer.type,
                id: currentCustomer.registrationNumber,
                name: currentCustomer.name,
                address: currentCustomer.address,
              }
            : {
                type: currentCustomer.type,
                id: currentCustomer.registrationNumber || "",
                name: currentCustomer.name,
              },
          purchaseOrderReference:
            guestName !== "" ? "Guest Name: " + guestName : "",
          purchaseOrderDescription: `Arrival Date ${arrivalDate} Departure Date ${departureDate} Room Number ${roomNumber}`,

          taxpayerActivityCode: branch.activityCode,
          internalID: `${prefix}${currentFolioNumber}`,

          // Zeros Fields
          totalDiscountAmount: 0,
          extraDiscountAmount: 0,
          totalItemsDiscountAmount: 0,

          // Calculated Fields
          invoiceLines: invoiceLines,
          netAmount: +totalNet.toFixed(5),
          totalSalesAmount: +totalSalesAmount.toFixed(5),
          totalAmount: +totalAmount.toFixed(5),
          taxTotals: [
            {
              taxType: "T1",
              amount: +totalTax.toFixed(5),
            },
            {
              taxType: "T9",
              amount: +totalService.toFixed(5),
            },
          ],
        };
        if (invoiceLines.length > 0) invoices.push(invoice);

        return invoices;
      };
    } catch (err: Error | any) {
      console.log(err);
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function getDocumentCustomer(
    accountNumber: string,
    firstName: string,
    lastName: string,
    customers: Customer[],
    identificationID: string
  ) {
    let currentCustomer;

    if (accountNumber !== "") {
      for (let i = 0; i < customers.length; i++) {
        if (customers[i].ArNumber === accountNumber + "") {
          currentCustomer = customers[i];

          // Use customer passport number in case of "F", and there is identificationID
          if (identificationID !== "" && currentCustomer.type === "F") {
            currentCustomer.registrationNumber = identificationID;
          }

          break;
        }
      }
    } else {
      if (identificationID !== "" && identificationID.length !== 14) {
        currentCustomer = {
          type: "F",
          registrationNumber: identificationID,
          name: (firstName + " " + lastName).trim(),
        };
      } else {
        currentCustomer = {
          // type: identificationID.length === 14 ? "P" : "F",
          // id: identificationID,
          type: "P",
          registrationNumber: "",
          name: (firstName + " " + lastName).trim(),
        };
      }
    }

    return currentCustomer;
  }

  async function getDocumentDateFormatted(dateNumber: number) {
    let converted_date = new Date(Math.round((dateNumber - 25569) * 864e5));
    let converted_date_str = String(converted_date).slice(4, 15);

    let date = converted_date_str.split(" ");
    let day = date[1];
    let month = date[0];
    let monthNumber =
      "JanFebMarAprMayJunJulAugSepOctNovDec".indexOf(month) / 3 + 1;
    let year = date[2];

    let result = String(day + "-" + monthNumber + "-" + year);
    return result;
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "internalID",
      numeric: false,
      disablePadding: false,
      label: "Folio Number",
    },
    {
      id: "type",
      numeric: false,
      disablePadding: false,
      label: "Type",
    },
    {
      id: "conceptID",
      numeric: false,
      disablePadding: false,
      label: "Concept",
    },
    {
      id: "netAmount",
      numeric: false,
      disablePadding: false,
      label: "Net Amount",
    },
    {
      id: "totalAmount",
      numeric: false,
      disablePadding: false,
      label: "Amount",
    },
    {
      id: "receiver",
      numeric: false,
      disablePadding: false,
      label: "Customer",
    },
    {
      id: "status",
      numeric: false,
      disablePadding: false,
      label: "Status",
    },
    {
      id: "reason",
      numeric: false,
      disablePadding: false,
      label: "Reason",
    },
    {
      id: "createdByName",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },

    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: false,
      disablePadding: false,
      label: "",
    },
  ];

  const lineHeadCells: readonly HeadCell[] = [
    {
      id: "internalCode",
      numeric: false,
      disablePadding: false,
      label: "Internal Code",
    },
    {
      id: "description",
      numeric: false,
      disablePadding: false,
      label: "Description",
    },
    {
      id: "itemCode",
      numeric: false,
      disablePadding: false,
      label: "Item Code",
    },
    {
      id: "itemType",
      numeric: false,
      disablePadding: false,
      label: "Item Type",
    },
    {
      id: "quantity",
      numeric: false,
      disablePadding: false,
      label: "Quantity",
    },
    {
      id: "netTotal",
      numeric: false,
      disablePadding: false,
      label: "Net Total",
    },
    {
      id: "total",
      numeric: false,
      disablePadding: false,
      label: "Total",
    },
    {
      id: "unitType",
      numeric: false,
      disablePadding: false,
      label: "Unit Type",
    },
    {
      id: "actions",
      numeric: false,
      disablePadding: false,
      label: "Actions",
    },
  ];

  const dataCells: readonly string[] = [
    "internalID",
    "type",
    "conceptID",
    "netAmount",
    "totalSalesAmount",
    "issuer",
    "receiver",
    "status",
    "reason",
  ];

  const options: any[] = [];

  if (listing)
    for (let option of listing) {
      options.push({ label: option.name, value: option.id });
    }

  const api: any = {};

  api[`${listingName}Listing`] = listing;
  api[`${listingName}Options`] = options;
  api[`${listingName}SearchText`] = searchText;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}LineHeadCells`] = lineHeadCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Selected`] = selected;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchAll`] = fetchAll;
  api[`${listingName}FetchStats`] = fetchStats;
  api[`${listingName}Get`] = get;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}CancelInvoice`] = cancelInvoice;
  api[`${listingName}ResubmitInvoice`] = resubmitInvoice;
  api[`${listingName}ApproveInvoice`] = approveInvoice;
  api[`${listingName}SubmitCreditNote`] = submitCreditNote;
  api[`${listingName}ImportData`] = importDataExcelFile;

  api[`${listingName}ChangeListing`] = (listing: Invoice[]) =>
    dispatch(setListing(listing));
  api[`${listingName}Search`] = (searchText: string) =>
    dispatch(setSearchText(searchText));
  api[`${listingName}Select`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));
  api[`${listingName}NextToken`] = nextToken;

  return api;
};

export default useInvoice;
